import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AlertService,
  CampaignService,
  FunnelStepService,
  OmpEditorHistoryService,
  OmpPreviewBridgeService,
  SideNavService,
} from '../_services';
import {
  Campaign,
  FunnelInputTypeEnum,
  FunnelStep,
  OmpDevicePreview,
  OMP_HISTORY_TOAST_MS,
  OmpHistoryAction,
  OmpHistoryVerb,
  OmpHostMessageType,
  OmpMoment,
  OmpPage,
  OmpPreviewMessage,
  OmpPreviewMessageType,
  OmpSectionPosition,
  OmpTab,
} from '../_models';
import { CanDeactivateServiceWrapper } from '../_helpers/can-deactivate-service-wrapper';
import { OmpSidebarComponent } from './omp-sidebar/omp-sidebar.component';
import { ompHistoryShortcut, ompIsDragControl, ompIsTextField, ompKeepsNativeUndo } from './omp-history-events';
import { buildOmpPreviewUrl, ompPreviewOrigin, OMP_PREVIEW_SUBDOMAIN_TYPE } from './omp-preview-url';
import {
  OmpDefaultMoment,
  OmpMomentKey,
  OmpPages,
  ompMomentLabel,
  ompMomentSectionKey,
  ompPageHasMoment,
  ompPageHasMoments,
  ompSectionName,
} from './order-management-portal.sections';

// Each OMP page previews the customer-portal step of a matching type.
const OMP_PAGE_STEP_TYPE: { [page: string]: FunnelInputTypeEnum } = {
  [OmpPage.Home]: FunnelInputTypeEnum.CustomerPortalHome,
  [OmpPage.Subscriptions]: FunnelInputTypeEnum.CustomerPortalSubscriptions,
  [OmpPage.SubscriptionDetails]: FunnelInputTypeEnum.CustomerPortalSubscriptionDetail,
  [OmpPage.Orders]: FunnelInputTypeEnum.CustomerPortalOrders,
  [OmpPage.OrderDetails]: FunnelInputTypeEnum.CustomerPortalOrderDetail,
  [OmpPage.Account]: FunnelInputTypeEnum.CustomerPortalPersonalInfo,
};

@Component({
  selector: 'app-order-management-portal',
  templateUrl: './order-management-portal.component.html',
  styleUrls: ['./order-management-portal.component.scss'],
  providers: [OmpPreviewBridgeService, OmpEditorHistoryService],
})
export class OrderManagementPortalComponent implements OnInit, AfterViewInit, OnDestroy {
  public hideWidgetBackground = true;
  public title = '';

  public activeDevice: OmpDevicePreview = OmpDevicePreview.Desktop;
  public activeTab: OmpTab = OmpTab.Sections;
  public selectedMoment: OmpMoment = OmpMoment.None;
  public selectedPage: OmpPage = OmpPage.Home;
  public devicePreview = OmpDevicePreview;

  public previewUrl: SafeResourceUrl = null;
  public previewReady = false;
  public previewError: string = null;
  private previewOrigin = '';

  public selectedProduct: any = null;
  public navigationEnabled = false;
  public livePreviewLoading = false;
  public livePreviewBlockedUrl = '';
  public productSelections: any[] = [];

  @ViewChild(OmpSidebarComponent, { static: false }) sidebar: OmpSidebarComponent;
  @ViewChild('ompPreviewIframe', { static: false }) previewIframe: ElementRef<HTMLIFrameElement>;

  private campaign: Campaign = null;
  private stepByType: { [type: number]: FunnelStep } = {};
  private previewRequestId = 0;
  private canDeactivateService = CanDeactivateServiceWrapper.getObject();
  private saveModalSub: Subscription = null;
  private destroyed$ = new Subject<void>();
  private dragging = false;
  public historyToast = '';
  public historyToastVisible = false;
  private historyToastTimer: any = null;
  private onTextInput = (event: Event) => {
    if (ompIsTextField(event.target)) {
      this.history.touchTextGroup();
    }
  };

  constructor(
    private sideNavService: SideNavService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private campaignService: CampaignService,
    private funnelStepService: FunnelStepService,
    private alertService: AlertService,
    private modalService: NgxSmartModalService,
    private previewBridge: OmpPreviewBridgeService,
    public history: OmpEditorHistoryService,
    private zone: NgZone,
    private host: ElementRef<HTMLElement>
  ) {}

  get campaignId() {
    return this.campaign ? this.campaign.id : null;
  }

  openProductPicker() {
    this.productSelections = this.selectedProduct ? [this.selectedProduct] : [];
    this.modalService.getModal('ompLivePreviewModal').close();
    this.modalService.getModal('ompPreviewProductModal').open();
  }

  onProductSelected(products: any[]) {
    if (products && products.length) {
      this.selectedProduct = products[0];
    }
    this.closeProductPicker();
  }

  get livePreviewProductLabel(): string {
    let label = 'Random product';
    if (this.selectedProduct && this.selectedProduct.name) {
      label = this.selectedProduct.name;
    }
    return label;
  }

  closeProductPicker() {
    this.modalService.getModal('ompPreviewProductModal').close();
    this.modalService.getModal('ompLivePreviewModal').open();
  }

  closeLivePreview() {
    this.previewRequestId++;
    this.closeLivePreviewModal();
  }

  get isFullScreen(): boolean {
    return this.activeDevice === OmpDevicePreview.FullScreen;
  }

  ngOnInit() {
    this.sideNavService.setSideNavExpanded(false);
    this.sideNavService.setShowSideNavToggle(false);

    this.canDeactivateService.setSaveModalEvent('', false);
    this.saveModalSub = this.canDeactivateService.getSaveModalEventObs().subscribe((data) => {
      if (data && data.event === 'save') {
        this.saveThenLeave();
      } else if (data && data.event === 'discard') {
        this.discardThenLeave();
      }
    });

    this.campaign = this.campaignService.getCurrentCampaign();
    this.buildPreviewUrl();
    this.zone.runOutsideAngular(() => document.addEventListener('input', this.onTextInput, true));
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const action = ompHistoryShortcut(event);
    const inEditor = event.target === document.body || this.host.nativeElement.contains(event.target as Node);
    if (action && inEditor && !ompKeepsNativeUndo(event.target)) {
      event.preventDefault();
      this.runHistory(action);
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    if (ompIsDragControl(event.target)) {
      this.dragging = true;
      this.history.beginGroup();
    }
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onPointerUp() {
    if (this.dragging) {
      this.dragging = false;
      setTimeout(() => this.history.endGroup());
    }
  }

  @HostListener('document:focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    if (ompIsTextField(event.target)) {
      this.history.endGroup();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private buildPreviewUrl() {
    const subdomain = this.campaign ? (this.campaign as any).subdomain : '';
    const url = buildOmpPreviewUrl(subdomain, OMP_PREVIEW_SUBDOMAIN_TYPE);
    if (url) {
      this.previewOrigin = ompPreviewOrigin(url);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.previewError = 'The preview is unavailable right now. Please try again in a while.';
      console.error('OMP preview: OMP_PREVIEW_URL is not set for this build.');
    }
  }

  ngAfterViewInit() {
    if (this.previewIframe && this.previewOrigin) {
      this.previewBridge.attach(this.previewIframe.nativeElement, this.previewOrigin);
      this.previewBridge.ready$.pipe(takeUntil(this.destroyed$)).subscribe((ready: boolean) => {
        this.previewReady = ready;
        if (ready) {
          this.previewError = null;
          this.previewBridge.send(OmpHostMessageType.Navigation, { enabled: this.navigationEnabled });
        }
      });
      this.previewBridge.timedOut$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        if (!this.previewReady) {
          this.previewError = 'The preview did not load. Please reload the page.';
          console.error('OMP preview: no handshake from ' + this.previewOrigin + ' before the timeout.');
        }
      });
      this.previewBridge.messages$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((message: OmpPreviewMessage) => this.onPreviewMessage(message));
    }
  }

  private onPreviewMessage(message: OmpPreviewMessage) {
    const payload = message.payload ? message.payload : {};
    switch (message.type) {
      case OmpPreviewMessageType.Select:
        this.revealSection(payload);
        break;
      case OmpPreviewMessageType.AddRequest:
        this.addSectionFromPreview(payload);
        break;
      case OmpPreviewMessageType.Reorder:
        this.reorderFromPreview(payload);
        break;
      case OmpPreviewMessageType.DeleteRequest:
        this.deleteFromPreview(payload);
        break;
      case OmpPreviewMessageType.Navigate:
        this.followPreviewNavigation(payload);
        break;
      case OmpPreviewMessageType.History:
        this.runHistory(payload.action);
        break;
      default:
        break;
    }
  }

  private revealSection(payload: any) {
    if (this.sidebar) {
      const page = payload.page as OmpPage;
      const moment = this.momentFromKey(payload.moment);
      this.activeTab = OmpTab.Sections;
      this.sidebar.requestReveal({ page: page, moment: moment, uid: payload.uid, childIndex: payload.childIndex });

      const pageChanged = page && page !== this.selectedPage;
      const momentChanged = moment !== this.selectedMoment;
      if (momentChanged) {
        this.onMomentChange(moment);
      }
      if (pageChanged) {
        this.onPageChange(page);
      }
      if (!pageChanged && !momentChanged) {
        this.sidebar.applyPendingReveal();
      }
    }
  }

  private followPreviewNavigation(payload: any) {
    const page = payload.page as OmpPage;
    if (page && page !== this.selectedPage) {
      this.selectedPage = page;
      if (!ompPageHasMoment(page, this.selectedMoment)) {
        this.onMomentChange(OmpDefaultMoment);
      }
    }
  }

  private addSectionFromPreview(payload: any) {
    if (this.sidebar) {
      this.activeTab = OmpTab.Sections;
      const zone = payload.zone === OmpSectionPosition.Top ? OmpSectionPosition.Top : OmpSectionPosition.Body;
      this.sidebar.requestAddSection(payload.anchorUid, payload.insert === 'before', zone);
    }
  }

  private deleteFromPreview(payload: any) {
    if (this.sidebar) {
      this.sidebar.requestDeleteSection(payload.uid);
    }
  }

  private reorderFromPreview(payload: any) {
    if (this.sidebar) {
      this.sidebar.applyPreviewReorder(payload.order ? payload.order : []);
    }
  }

  private momentFromKey(key: string): OmpMoment {
    let moment = this.selectedMoment;
    Object.keys(OmpMomentKey).forEach((value) => {
      if (OmpMomentKey[value] === key) {
        moment = Number(value) as OmpMoment;
      }
    });
    return moment;
  }

  private previewProductId() {
    return this.selectedProduct ? this.selectedProduct.id : null;
  }

  openLivePreview() {
    if (this.sidebar && this.sidebar.hasChanges) {
      this.alertService.error('Save your changes first — the live preview renders the saved configuration.');
    } else {
      this.modalService.getModal('ompLivePreviewModal').open();
    }
  }

  confirmLivePreview() {
    this.livePreviewLoading = true;
    this.livePreviewBlockedUrl = '';
    this.loadPreview();
  }

  private openLivePreviewTab(url: string) {
    this.livePreviewLoading = false;
    const opened = window.open(url, '_blank');
    if (opened) {
      this.closeLivePreviewModal();
    } else {
      this.livePreviewBlockedUrl = url;
    }
  }

  private closeLivePreviewModal() {
    this.livePreviewLoading = false;
    this.livePreviewBlockedUrl = '';
    this.modalService.getModal('ompLivePreviewModal').close();
  }

  private failLivePreview(message: string) {
    this.livePreviewLoading = false;
    this.alertService.error(message);
  }

  onNavigationToggle(enabled: boolean) {
    this.navigationEnabled = enabled;
    this.previewBridge.send(OmpHostMessageType.Navigation, { enabled: enabled });
  }

  private loadPreview() {
    const funnelId = this.campaign ? this.campaign.customer_portal_id : null;
    const type = OMP_PAGE_STEP_TYPE[this.selectedPage];
    const reqId = ++this.previewRequestId;

    if (funnelId && (type || type === 0)) {
      const cached = this.stepByType[type];
      if (cached) {
        this.generateStepPreview(cached, funnelId, reqId);
      } else {
        this.funnelStepService.listAll({ funnel: funnelId, type: type }).subscribe(
          (steps: FunnelStep[]) => {
            let step: FunnelStep = null;
            if (steps && steps.length) {
              step = steps.find((s) => s.type === type) || steps[0];
            }
            if (step) {
              this.stepByType[type] = step;
              this.generateStepPreview(step, funnelId, reqId);
            } else if (reqId === this.previewRequestId) {
              this.failLivePreview('No preview is available for this page.');
            }
          },
          (error) => {
            if (reqId === this.previewRequestId) {
              this.failLivePreview("We couldn't open the preview. Please try again.");
            }
          }
        );
      }
    } else {
      this.failLivePreview('No preview is available for this page.');
    }
  }

  private generateStepPreview(step: FunnelStep, funnelId: string | number, reqId: number) {
    this.funnelStepService.setFunnelId(funnelId);
    let momentStatus: OmpMoment;
    if (ompPageHasMoments(this.selectedPage)) {
      momentStatus = this.selectedMoment;
    }
    this.funnelStepService.generatePreview(step.id, this.campaign.id, this.previewProductId(), momentStatus).subscribe(
      (url: any) => {
        if (reqId === this.previewRequestId) {
          this.openLivePreviewTab(this.toReactPreviewUrl(url.toString()));
        }
      },
      (error) => {
        if (reqId === this.previewRequestId) {
          this.failLivePreview("We couldn't create a preview session. Please try again.");
        }
      }
    );
  }

  private toReactPreviewUrl(url: string): string {
    let result = url;
    try {
      const parsed = new URL(url);
      const parts = parsed.hostname.split('.');
      if (parts.length > 1 && parts[1] !== 'react') {
        parts.splice(1, 0, 'react');
        parsed.hostname = parts.join('.');
      }
      result = parsed.toString();
    } catch (e) {
      result = url;
    }
    return result;
  }

  ngOnDestroy() {
    document.removeEventListener('input', this.onTextInput, true);
    clearTimeout(this.historyToastTimer);
    this.history.clear();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.previewBridge.detach();
    this.sideNavService.setSideNavExpanded(true);
    this.sideNavService.setShowSideNavToggle(true);
    this.canDeactivateService.setFormDirty(false);
    if (this.saveModalSub) {
      this.saveModalSub.unsubscribe();
    }
  }

  get canSave(): boolean {
    let value = false;
    if (this.sidebar) {
      value = this.sidebar.canSave;
    }
    return value;
  }

  get hasChanges(): boolean {
    return !!this.sidebar && this.sidebar.hasChanges;
  }

  get saving(): boolean {
    return !!this.sidebar && this.sidebar.saving;
  }

  get saveIssues(): string[] {
    let issues: string[] = [];
    if (this.sidebar) {
      issues = this.sidebar.labelIssueMessages;
    }
    return issues;
  }

  private saveThenLeave() {
    const returnUrl = this.canDeactivateService.getCurrentRouteTo();
    if (this.sidebar) {
      this.sidebar.save(() => {
        this.canDeactivateService.setFormDirty(false);
        if (returnUrl) {
          this.router.navigate([returnUrl]);
        }
      });
    }
  }

  private discardThenLeave() {
    const returnUrl = this.canDeactivateService.getCurrentRouteTo();
    this.canDeactivateService.setFormDirty(false);
    if (returnUrl) {
      this.router.navigate([returnUrl]);
    }
  }

  get isDefaultMoment(): boolean {
    return this.selectedMoment === OmpDefaultMoment;
  }

  get syncMomentsTitle(): string {
    let title = "Sync this moment's sections with the Default moment?";
    if (this.isDefaultMoment) {
      title = "Sync this moment's sections to all moments?";
    }
    return title;
  }

  get selectedPageLabel(): string {
    let label = '';
    OmpPages.forEach((page) => {
      if (page.key === this.selectedPage) {
        label = page.label;
      }
    });
    return label;
  }

  get syncMomentsMessage(): string {
    let message =
      'The ' +
      this.selectedPageLabel +
      ' page for ' +
      ompMomentLabel(this.selectedMoment) +
      " will be replaced with the Default moment's sections, their order and their settings. " +
      'Anything currently configured for this moment is overwritten.';
    if (this.isDefaultMoment) {
      message =
        "Every other moment's " +
        this.selectedPageLabel +
        " page will be replaced with this moment's sections, their order and their " +
        'settings. Anything currently configured for those moments is overwritten.';
    }
    return message;
  }

  get syncMomentsControlsNote(): string {
    let note = 'this moment keeps';
    if (this.isDefaultMoment) {
      note = 'each moment keeps';
    }
    return note;
  }

  get syncMomentsControlsName(): string {
    return ompSectionName(ompMomentSectionKey(this.selectedPage), this.selectedPage);
  }

  promptSyncMoments() {
    this.modalService.getModal('ompSyncMomentsModal').open();
  }

  cancelSyncMoments() {
    this.modalService.getModal('ompSyncMomentsModal').close();
  }

  applySyncMoments() {
    this.modalService.getModal('ompSyncMomentsModal').close();
    if (this.sidebar) {
      this.sidebar.syncSectionsFromDefault(this.selectedMoment);
      let message = 'Sections synced from the Default moment.';
      if (this.isDefaultMoment) {
        message = 'Sections synced to all other moments.';
      }
      this.alertService.success(message, true);
    }
  }

  onTabChange(tab: OmpTab) {
    this.activeTab = tab;
  }

  onMomentChange(moment: OmpMoment) {
    this.selectedMoment = moment;
    this.previewBridge.send(OmpHostMessageType.Moment, { moment: OmpMomentKey[moment] });
  }

  onRequestPage(target: { page: OmpPage; moment: OmpMoment }) {
    this.selectedMoment = target.moment;
    this.previewBridge.send(OmpHostMessageType.Moment, { moment: OmpMomentKey[target.moment] });
    if (this.selectedPage !== target.page) {
      this.onPageChange(target.page);
    }
  }

  onPageChange(page: OmpPage) {
    this.selectedPage = page;
    if (!ompPageHasMoment(page, this.selectedMoment)) {
      this.onMomentChange(OmpDefaultMoment);
    }
    this.previewBridge.send(OmpHostMessageType.Page, { page: page });
  }

  onDeviceChange(device: OmpDevicePreview) {
    this.activeDevice = device;
    this.previewBridge.send(OmpHostMessageType.Device, { device: device });
  }

  onSave() {
    if (this.sidebar) {
      this.sidebar.save();
    }
  }

  onReset() {
    if (this.hasChanges && !this.saving) {
      this.sidebar.promptReset();
    }
  }

  onUndo() {
    this.runHistory(OmpHistoryAction.Undo);
  }

  onRedo() {
    this.runHistory(OmpHistoryAction.Redo);
  }

  private runHistory(action: OmpHistoryAction) {
    const blocked = !this.sidebar || this.saving || this.modalService.getOpenedModals().length > 0;
    if (!blocked && action === OmpHistoryAction.Undo && this.history.canUndo) {
      const label = this.history.undoLabel;
      this.sidebar.undo();
      this.showHistoryToast(OmpHistoryVerb.Undid + ': ' + label);
    } else if (!blocked && action === OmpHistoryAction.Redo && this.history.canRedo) {
      const label = this.history.redoLabel;
      this.sidebar.redo();
      this.showHistoryToast(OmpHistoryVerb.Redid + ': ' + label);
    }
  }

  private showHistoryToast(text: string) {
    this.historyToast = text;
    this.historyToastVisible = true;
    clearTimeout(this.historyToastTimer);
    this.historyToastTimer = setTimeout(() => {
      this.historyToastVisible = false;
    }, OMP_HISTORY_TOAST_MS);
  }
}
